/* ==========================================================================
   Rynoka: toyland-pincode-page.js — Delivery / Pincode checker page
   Section: sections/page-pincode-checker.liquid

   Self-contained on purpose: it does NOT call into toyland-scripts.js. That file
   owns the PDP checker (#tl-pincode-input / [data-tl-pincode-btn]) and binds its
   own delegated listeners; this page uses a different DOM contract entirely
   ([data-tl-pin-*]) so the two never fight over the same elements or over
   script-load order.

   It DOES read the same global serviceability config
   (#tl-pincode-checker-config-global, rendered by snippets/toyland-pincode-config-global.liquid)
   so the live Shiprocket lookup, pickup pincode and shipment weight stay
   configured in one place — Theme settings.

   All shopper-facing strings come from the section's #tl-pin-copy JSON, so every
   word stays admin-editable (multi-department mandate).
   ========================================================================== */

(function () {
  'use strict';

  var ICONS = {
    check: '<polyline points="20 6 9 17 4 12"/>',
    x: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
    truck: '<rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h3l3 3v5h-6V8z"/><circle cx="6" cy="18" r="1.8"/><circle cx="17.5" cy="18" r="1.8"/>',
    card: '<rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>',
    wallet: '<path d="M20 12V8H6a2 2 0 0 1 0-4h12v4"/><path d="M4 6v12a2 2 0 0 0 2 2h14v-4"/><path d="M18 12a2 2 0 0 0 0 4h4v-4z"/>'
  };

  var COPY_DEFAULT = {
    errInvalid: 'Please enter a valid 6-digit pincode.',
    errLeadingZero: 'No Indian pincode starts with 0 — please double-check.',
    checking: 'Checking…',
    okTitle: 'Delivering to {pin}',
    lineEta: 'Estimated delivery in {days} business days',
    lineCodYes: 'Cash on Delivery available',
    lineCodNo: 'Cash on Delivery not available at this pincode',
    /* Deliberately carries no amount. The real string is supplied by
       [data-tl-pin-copy] from Theme settings → Rynoka — Delivery via the
       {{free_ship}} token; this fallback only fires if that JSON block is missing,
       and a stale number here would outlive every threshold change. */
    lineFree: 'Free delivery on qualifying orders',
    badTitle: 'We don’t deliver to {pin} yet',
    badNote: 'We’re expanding fast — check back soon, or reach out and we’ll notify you when we do.',
    errTitle: 'Couldn’t check {pin} right now',
    daysMetro: '1–2',
    daysTier2: '2–3',
    daysTier3: '3–5',
    daysNe: '5–7'
  };

  var CFG_DEFAULT = {
    serviceabilityMode: 'shiprocket',
    shiprocketServiceUrl: '',
    pickupPincode: '395007',
    weightKg: 0.5,
    errNotServiceable: 'Sorry, we do not deliver to this pincode yet.',
    errNetwork: 'Could not check delivery right now. Please try again.',
    errNotConfigured: 'Delivery check is temporarily unavailable. Please try again later or contact us.'
  };

  /* Zone buckets by 3-digit prefix — the fallback ETA when the serviceability
     service returns no day count. Same table as toyland-scripts.js; duplicated
     rather than imported to keep this file independent of load order. */
  var NE_PREFIXES = [787, 788, 789, 790, 791, 792, 793, 794, 795, 796, 797, 798, 799, 744];
  var METRO_PREFIXES = [110, 111, 400, 401, 402, 403, 404, 405, 406, 407, 408, 410, 411, 412, 413, 414,
    560, 561, 562, 563, 564, 565, 566, 567, 568, 600, 601, 602, 603, 604, 605, 606, 607, 608, 609, 610,
    611, 612, 613, 614, 615, 616, 617, 618, 619, 700, 701, 702, 703, 704, 705, 706, 707, 708, 500, 501,
    502, 503, 504, 505, 506, 380, 381, 382, 383, 384, 385, 390, 391, 392, 393, 394, 395];
  var TIER2_PREFIXES = [302, 303, 304, 305, 306, 226, 227, 228, 229, 800, 801, 802, 803, 804, 462, 463,
    464, 465, 452, 453, 454, 455, 440, 441, 442, 443, 444, 445, 641, 642, 643, 644, 645, 682, 683, 684,
    685, 686, 160, 161, 162, 163, 248, 249, 250, 670, 671, 672, 673, 571, 572, 573, 520, 521, 522, 523,
    530, 531, 532, 480, 481, 482, 483, 484, 485, 360, 361, 362, 363];

  function zoneOf(pin) {
    var p3 = parseInt(pin.slice(0, 3), 10);
    if (NE_PREFIXES.indexOf(p3) > -1) return 'ne';
    if (METRO_PREFIXES.indexOf(p3) > -1) return 'metro';
    if (TIER2_PREFIXES.indexOf(p3) > -1) return 'tier2';
    return 'tier3';
  }

  function zoneDays(pin, copy) {
    switch (zoneOf(pin)) {
      case 'ne': return copy.daysNe;
      case 'metro': return copy.daysMetro;
      case 'tier2': return copy.daysTier2;
      default: return copy.daysTier3;
    }
  }

  function readJson(el, fallback) {
    if (!el) return Object.assign({}, fallback);
    try {
      return Object.assign({}, fallback, JSON.parse(el.textContent || '{}'));
    } catch (e) {
      return Object.assign({}, fallback);
    }
  }

  function fill(tpl, vars) {
    return String(tpl == null ? '' : tpl).replace(/\{(\w+)\}/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : m;
    });
  }

  function svg(paths, size) {
    var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    s.setAttribute('width', size);
    s.setAttribute('height', size);
    s.setAttribute('viewBox', '0 0 24 24');
    s.setAttribute('fill', 'none');
    s.setAttribute('stroke', 'currentColor');
    s.setAttribute('stroke-width', '2.3');
    s.setAttribute('stroke-linecap', 'round');
    s.setAttribute('stroke-linejoin', 'round');
    s.setAttribute('aria-hidden', 'true');
    s.innerHTML = paths;
    return s;
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  /** Panel shell: coloured box + round icon badge + title. */
  function panel(variant, iconPaths, title) {
    var box = el('div', 'tl-pin-panel tl-pin-panel--' + variant);
    var head = el('div', 'tl-pin-panel__head');
    var badge = el('span', 'tl-pin-panel__badge');
    badge.appendChild(svg(iconPaths, 16));
    head.appendChild(badge);
    head.appendChild(el('p', 'tl-pin-panel__title', title));
    box.appendChild(head);
    return box;
  }

  function line(iconPaths, text) {
    var row = el('span', 'tl-pin-panel__line');
    row.appendChild(svg(iconPaths, 15));
    row.appendChild(document.createTextNode(text));
    return row;
  }

  document.querySelectorAll('[data-tl-pin]').forEach(function (root) {
    var form = root.querySelector('[data-tl-pin-form]');
    var input = root.querySelector('[data-tl-pin-input]');
    var btn = root.querySelector('[data-tl-pin-btn]');
    var errEl = root.querySelector('[data-tl-pin-error]');
    var resultEl = root.querySelector('[data-tl-pin-result]');
    if (!form || !input || !resultEl) return;

    var copy = readJson(root.querySelector('[data-tl-pin-copy]'), COPY_DEFAULT);
    var cfg = readJson(document.getElementById('tl-pincode-checker-config-global'), CFG_DEFAULT);
    var busy = false;

    function showError(text) {
      resultEl.textContent = '';
      if (!errEl) return;
      errEl.textContent = text;
      errEl.hidden = false;
    }

    function clearError() {
      if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
    }

    function setBusy(on) {
      busy = on;
      if (!btn) return;
      btn.disabled = on;
      if (on) {
        btn.dataset.tlPinLabel = btn.textContent;
        btn.textContent = copy.checking;
      } else if (btn.dataset.tlPinLabel) {
        btn.textContent = btn.dataset.tlPinLabel;
        delete btn.dataset.tlPinLabel;
      }
    }

    function renderOk(pin, days, cod) {
      var box = panel('ok', ICONS.check, fill(copy.okTitle, { pin: pin }));
      var lines = el('div', 'tl-pin-panel__lines');
      if (days) lines.appendChild(line(ICONS.truck, fill(copy.lineEta, { days: days, pin: pin })));
      lines.appendChild(line(ICONS.card, cod ? copy.lineCodYes : copy.lineCodNo));
      if (copy.lineFree) lines.appendChild(line(ICONS.wallet, copy.lineFree));
      box.appendChild(lines);
      resultEl.textContent = '';
      resultEl.appendChild(box);
    }

    function renderBad(pin, title, note) {
      var box = panel('bad', ICONS.x, fill(title, { pin: pin }));
      if (note) box.appendChild(el('p', 'tl-pin-panel__note', note));
      resultEl.textContent = '';
      resultEl.appendChild(box);
    }

    /* Live serviceability lookup. Returns { serviceable, eddDays, codAvailable }
       on success, or { error: 'network' | 'not_configured' }. */
    function lookup(pin) {
      var base = String(cfg.shiprocketServiceUrl || '').trim().replace(/\/$/, '');
      if (!base) return Promise.resolve({ error: 'not_configured' });

      var qs = new URLSearchParams({
        delivery_postcode: pin,
        pickup_postcode: String(cfg.pickupPincode || '395007').replace(/\D/g, ''),
        weight: String(cfg.weightKg == null ? 0.5 : cfg.weightKg),
        cod: '1'
      });
      var url = base + '?' + qs;
      var crossOrigin = url.indexOf('http') === 0 && url.indexOf(window.location.origin) !== 0;

      return fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: crossOrigin ? 'omit' : 'same-origin'
      })
        .then(function (res) {
          if (res.status === 404 || res.status === 502 || res.status === 503) {
            return { error: 'not_configured' };
          }
          if (!res.ok) return { error: 'network' };
          return res.json().then(
            function (data) {
              return data && typeof data === 'object' ? data : { error: 'not_configured' };
            },
            function () { return { error: 'network' }; }
          );
        })
        .catch(function () { return { error: 'network' }; });
    }

    function check() {
      if (busy) return;
      var pin = (input.value || '').replace(/\D/g, '');

      if (pin.length !== 6) { showError(copy.errInvalid); input.focus(); return; }
      if (pin.charAt(0) === '0') { showError(copy.errLeadingZero); input.focus(); return; }

      clearError();

      // Zone mode is the offline/estimate mode — no request, answer immediately.
      if (cfg.serviceabilityMode === 'zones') {
        renderOk(pin, zoneDays(pin, copy), true);
        return;
      }

      setBusy(true);
      resultEl.textContent = '';

      lookup(pin).then(function (res) {
        setBusy(false);
        if (res.error === 'not_configured') {
          renderBad(pin, copy.errTitle, cfg.errNotConfigured);
          return;
        }
        if (res.error) {
          renderBad(pin, copy.errTitle, cfg.errNetwork);
          return;
        }
        if (res.serviceable !== true) {
          renderBad(pin, copy.badTitle, copy.badNote || cfg.errNotServiceable);
          return;
        }
        renderOk(pin, res.eddDays ? String(res.eddDays) : zoneDays(pin, copy), res.codAvailable !== false);
      });
    }

    // Digits only, max 6 — typed, pasted or autofilled.
    input.addEventListener('input', function () {
      var cleaned = input.value.replace(/\D/g, '').slice(0, 6);
      if (input.value !== cleaned) input.value = cleaned;
      clearError();
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      check();
    });

    root.addEventListener('click', function (e) {
      var sample = e.target.closest('[data-tl-pin-sample]');
      if (!sample) return;
      input.value = sample.getAttribute('data-tl-pin-sample');
      clearError();
      check();
    });
  });
})();
